import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { showNotification } from "@mantine/notifications";
import { customAxios } from "lib/axios";

export interface TodayAttendee {
  user: string | Record<string, any>;
  userType?: "User" | "Client" | string;
  status?: "pending" | "accepted" | "declined" | string;
  joinedAt?: string;
}

export interface RecurringPattern {
  frequency?: string;
  interval?: number;
  endDate?: string;
  daysOfWeek?: number[];
}

export interface Reminder {
  type?: string;
  time?: number;
  sent?: boolean;
}

export interface Attachment {
  filename?: string;
  url?: string;
  uploadedBy?: string;
  uploadedAt?: string;
}

export interface MeetingToday {
  _id?: string;
  id?: string;
  title?: string;
  description?: string;
  organizer?: string | Record<string, any>;
  attendees?: TodayAttendee[];
  scheduledDate?: string;
  duration?: number;
  meetingType?: string;
  status?: string;
  meetingLink?: string;
  meetingPlatform?: string;
  projectId?: string | Record<string, any>;
  organizationId?: string;
  agenda?: string;
  meetingRoom?: string;
  isRecurring?: boolean;
  recurringPattern?: RecurringPattern;
  reminders?: Reminder[];
  notes?: string;
  recordingUrl?: string;
  attachments?: Attachment[];
  createdAt?: string;
  updatedAt?: string;
}

export type TodayMeetingsResponse = {
  success: boolean;
  data: MeetingToday[];
  message?: string;
};

const fetchTodayMeetings = async (): Promise<TodayMeetingsResponse> =>
  (await customAxios.get<TodayMeetingsResponse>("/meetings/today")).data;

export const useGetTodayMeetings = () =>
  useQuery<TodayMeetingsResponse, AxiosError>(
    ["meetings", "today"],
    fetchTodayMeetings,
    {
      onError: (error) =>
        showNotification({
          title: "Error Loading Today's Meetings",
          message: "Failed to load today's meetings",
          color: "red",
        }),
      staleTime: 0,
      retry: 1,
      refetchOnMount: "always",
    }
  );
